import { useId, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { Crown, Loader2, Mail, UserMinus, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/client/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/client/components/ui/dialog";
import { Input } from "@/client/components/ui/input";
import { Label } from "@/client/components/ui/label";
import { Separator } from "@/client/components/ui/separator";
import { api, describeError, unwrap } from "@/client/lib/api";
import { forgetStory, refreshPeople, sessionUser } from "@/client/db";
import { usePeople } from "@/client/hooks/use-document";

interface MembersDialogProps {
  storyId: string;
  storyName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Who's on the story. The owner invites existing users by email and removes
 * editors; editors can leave. Invitations show up in the invitee's story list.
 */
export function MembersDialog({ storyId, storyName, open, onOpenChange }: MembersDialogProps) {
  const id = useId();
  const navigate = useNavigate();
  const people = usePeople();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const isOwner = people?.role === "owner";
  const me = sessionUser().id;

  const run = async (key: string, work: () => Promise<unknown>, success?: string) => {
    setBusy(key);
    try {
      await work();
      await refreshPeople(storyId);
      if (success) toast.success(success);
      return true;
    } catch (error) {
      toast.error(describeError(error));
      return false;
    } finally {
      setBusy(null);
    }
  };

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    setInviteError(null);
    setBusy("invite");
    try {
      await unwrap(api.api.stories({ id: storyId }).invitations.post({ email }));
      await refreshPeople(storyId);
      toast.success(`Invited ${email}`, { description: "It shows up in their story list." });
      setEmail("");
    } catch (error) {
      setInviteError(describeError(error));
    } finally {
      setBusy(null);
    }
  };

  const handleLeave = async () => {
    setBusy("leave");
    try {
      await unwrap(api.api.stories({ id: storyId }).leave.post());
      onOpenChange(false);
      forgetStory(storyId);
      toast.success(`You left ${storyName}`);
      await navigate({ to: "/" });
    } catch (error) {
      toast.error(describeError(error));
    } finally {
      setBusy(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Collaborators</DialogTitle>
          <DialogDescription>
            Editors can change everything in {storyName} except deleting it or managing who's on it.
          </DialogDescription>
        </DialogHeader>

        {!people ? (
          <div className="flex justify-center py-6">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="grid gap-4">
            <ul className="grid gap-2">
              <li className="flex items-center justify-between gap-2 text-sm">
                <span>
                  {people.owner.name}
                  {people.owner.id === me && " (you)"}
                  <span className="ml-2 text-muted-foreground">{people.owner.email}</span>
                </span>
                <span className="flex items-center gap-1 text-xs text-muted-foreground">
                  <Crown className="h-3 w-3" /> Owner
                </span>
              </li>
              {people.editors.map((editor) => (
                <li key={editor.id} className="flex items-center justify-between gap-2 text-sm">
                  <span>
                    {editor.name}
                    {editor.id === me && " (you)"}
                    <span className="ml-2 text-muted-foreground">{editor.email}</span>
                  </span>
                  {isOwner ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7"
                      disabled={busy !== null}
                      onClick={() =>
                        void run(
                          editor.id,
                          () => unwrap(api.api.stories({ id: storyId }).members({ userId: editor.id }).delete()),
                          `Removed ${editor.name}`
                        )
                      }
                    >
                      {busy === editor.id ? <Loader2 className="animate-spin" /> : <UserMinus />}
                      Remove
                    </Button>
                  ) : (
                    <span className="text-xs text-muted-foreground">Editor</span>
                  )}
                </li>
              ))}
            </ul>

            {isOwner && people.invitations.length > 0 && (
              <>
                <Separator />
                <div className="grid gap-2">
                  <p className="text-xs font-medium text-muted-foreground">Pending invitations</p>
                  {people.invitations.map((invitation) => (
                    <div key={invitation.id} className="flex items-center justify-between gap-2 text-sm">
                      <span className="flex items-center gap-2">
                        <Mail className="h-3.5 w-3.5 text-muted-foreground" />
                        {invitation.name}
                        <span className="text-muted-foreground">{invitation.email}</span>
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7"
                        disabled={busy !== null}
                        onClick={() =>
                          void run(invitation.id, () =>
                            unwrap(
                              api.api
                                .stories({ id: storyId })
                                .invitations({ invitationId: invitation.id })
                                .revoke.post()
                            )
                          )
                        }
                      >
                        {busy === invitation.id ? <Loader2 className="animate-spin" /> : <X />}
                        Revoke
                      </Button>
                    </div>
                  ))}
                </div>
              </>
            )}

            <Separator />
            {isOwner ? (
              <form onSubmit={handleInvite} className="grid gap-2">
                <Label htmlFor={`${id}-email`}>Invite an editor</Label>
                <div className="flex gap-2">
                  <Input
                    id={`${id}-email`}
                    type="email"
                    placeholder="Their email on this instance"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                  <Button type="submit" disabled={busy !== null || !email.trim()}>
                    {busy === "invite" ? <Loader2 className="animate-spin" /> : "Invite"}
                  </Button>
                </div>
                {inviteError ? (
                  <p role="alert" className="text-xs text-destructive">
                    {inviteError}
                  </p>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    They need an account here first. The administrator can send them a setup link.
                  </p>
                )}
              </form>
            ) : (
              <Button variant="outline" disabled={busy !== null} onClick={() => void handleLeave()}>
                {busy === "leave" && <Loader2 className="animate-spin" />}
                Leave this story
              </Button>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
